import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, InputAdornment } from '@mui/material';
import TabSelectAutocomplete from './TabSelectAutocomplete';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import styles from '../../../../../index.module.scss';
import { AccountQB } from '../../clients-list';

interface AccountAutocompleteProps {
  name: string;
  control: any;
  accountList: AccountQB[];
  label: string;
  required?: boolean;
  disabled?: boolean;
  filterAP_AR?: boolean;
}

// Subaccounts show their full path, e.g. "Job Materials:Plants and Soil", so a user can
// tell which account they are choosing.
export const accountLabel = (account: AccountQB) =>
  `${account.AcctNum ? `${account.AcctNum} | ` : ''}${account.FullyQualifiedName || account.Name}`;

// Compact list: a long name wraps with tight lines, and a little padding plus a faint
// divider keeps each account clearly separate from the next.
export const accountListSx = {
  py: 0,
  '& .MuiAutocomplete-option': {
    fontSize: '0.8125rem',
    lineHeight: 1.25,
    minHeight: 'auto',
    py: '5px',
    borderBottom: '1px solid #eef1f5'
  },
  '& .MuiAutocomplete-option:last-of-type': { borderBottom: 'none' }
};

const AccountAutocomplete: React.FC<AccountAutocompleteProps> = ({
  name,
  control,
  accountList,
  label,
  required,
  disabled,
  filterAP_AR = true
}) => {
  const [open, setOpen] = useState(false);

  const filteredAccounts = filterAP_AR
    ? accountList?.filter(
        (account) => account.AccountType !== 'Accounts Receivable' && account.AccountType !== 'Accounts Payable'
      )
    : accountList;
  // Sorting by full name keeps each subaccount directly under its parent.
  const options = [...(filteredAccounts || [])].sort((a, b) =>
    (a.FullyQualifiedName || a.Name).localeCompare(b.FullyQualifiedName || b.Name)
  );
  // A saved template may hold an older copy of the account; show the current full name.
  const current = (value?: AccountQB | null) => (value && options.find((o) => o.Id === value.Id)) || value || null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TabSelectAutocomplete
          {...field}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(_, newValue) => field.onChange(newValue)}
          options={options}
          value={current(field.value) as AccountQB}
          isOptionEqualToValue={(option, value) => option.Id === value?.Id}
          disabled={disabled || !options.length}
          disableClearable
          getOptionLabel={(option: AccountQB) => accountLabel(option)}
          ListboxProps={{ sx: accountListSx }}
          sx={{
            fontSize: '1rem',
            margin: '0.5rem 0',
            border: `1px solid ${styles.lightGrey}`,
            borderRadius: '.5rem'
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required={required}
              label={label}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <ArrowForwardIosIcon
                      sx={{ fontSize: '1rem', cursor: disabled ? 'default' : 'pointer' }}
                      onClick={() => {
                        if (!disabled) {
                          setOpen((prev) => !prev);
                        }
                      }}
                    />
                  </InputAdornment>
                )
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.Id}>
              {accountLabel(option)}
            </li>
          )}
        />
      )}
    />
  );
};

export default AccountAutocomplete;
